import { ILeaf } from 'shared/types/tree'
import {
  makeKiddomDeleteRequest,
  makeKiddomPostRequest,
  makeKiddomPutRequest,
} from 'infrastructure/api'
import { useCallback } from 'react'
import { useWithErrorNotification } from 'shared/ui/GlobalDialog'
import { doInBatches } from 'shared/utils'

type TreeIds = {
  tree_id: string
  version: string
  id: string
}

export type LeafRequestParams = Partial<ILeaf> & TreeIds
export type LeafCreateParams = { leaves: Partial<ILeaf>[] } & TreeIds

function createLeafRoute({ tree_id, version, id }: TreeIds) {
  return `/api/tree/${tree_id}:${version}/node/${id}/leaf`
}

function updateLeafRoute({ tree_id, version, id }: TreeIds) {
  return `/api/tree/${tree_id}:${version}/leaf/${id}`
}

function deleteLeafRoute({
  treeId,
  versionId,
  leafId,
}: {
  treeId: string
  versionId: string
  leafId: string
}) {
  return `/api/tree/${treeId}:${versionId}/leaf/${leafId}`
}

function createLeaf({ tree_id, version, id, ...rest }: LeafCreateParams) {
  return makeKiddomPostRequest({
    kiddomPath: createLeafRoute({ tree_id, version, id }),
    requestBody: rest,
  })
}

function updateLeafData({ tree_id, version, id, ...rest }: LeafRequestParams) {
  return makeKiddomPutRequest({
    kiddomPath: updateLeafRoute({ tree_id, version, id }),
    requestBody: rest,
  })
}

function deleteLeaf(params: {
  treeId: string
  versionId: string
  leafId: string
}) {
  const kiddomPath = deleteLeafRoute(params)
  return makeKiddomDeleteRequest<void>({ kiddomPath })
}

function _deleteLeaf(leaf: { id: string; tree_id: string; version: string }) {
  const params = {
    leafId: leaf.id,
    treeId: leaf.tree_id,
    versionId: leaf.version,
  }
  return deleteLeaf(params)
}

export async function deleteLeaves(
  ...leaves: { id: string; tree_id: string; version: string }[]
) {
  await doInBatches(leaves, _deleteLeaf)
}

export function useCreateLeaf() {
  const request = useWithErrorNotification(
    { errorTitle: 'Error Creating Leaf' },
    createLeaf,
  )
  return useCallback(
    (params: LeafCreateParams) => {
      return request(params)
    },
    [request],
  )
}

export function useUpdateLeafData() {
  const request = useWithErrorNotification(
    { errorTitle: 'Error Updating Leaf' },
    updateLeafData,
  )
  return useCallback(
    (params: LeafRequestParams) => {
      return request(params)
    },
    [request],
  )
}
